import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import ErrorBoundary from './components/ErrorBoundary';
import { createAppTheme } from './theme';
import { ThemeProvider, useThemeContext } from './contexts/ThemeContext';
import './aws-config';

import App from './App';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Smart retry logic
      retry: (failureCount, error: any) => {
        // Never retry authentication errors
        if (error?.message?.includes('Authentication required') || 
            error?.message?.includes('401') || 
            error?.message?.includes('403')) {
          console.log('Skipping retry for authentication error');
          return false;
        }
        
        // Don't retry on 4xx client errors (except auth)
        if (error?.message?.includes('HTTP 4')) {
          console.log('Skipping retry for client error');
          return false;
        }
        
        // Retry network and 5xx errors up to 2 times
        if (failureCount < 2) {
          console.log(`Retrying failed request (attempt ${failureCount + 1})`);
          return true;
        }
        
        return false;
      },
      
      // Retry delay - exponential backoff
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // Don't refetch on window focus to avoid auth issues
      refetchOnWindowFocus: false,
      
      // Conservative stale time
      staleTime: 30 * 1000, // 30 seconds
      
      // Conservative cache time - use gcTime for v5
      gcTime: 5 * 60 * 1000, // 5 minutes
    },
    
    mutations: {
      // Smart retry logic for mutations
      retry: (failureCount, error: any) => {
        // Never retry authentication errors
        if (error?.message?.includes('Authentication required') || 
            error?.message?.includes('401') || 
            error?.message?.includes('403')) {
          return false;
        }
        
        // Only retry network errors and 5xx for mutations
        if ((error?.message?.includes('Network') || 
             error?.message?.includes('HTTP 5')) && 
            failureCount < 1) {
          return true;
        }
        
        return false;
      },
      
      // Retry delay for mutations
      retryDelay: 2000, // 2 seconds fixed delay for mutations
    },
  },
});

function AppWithTheme() {
  const { themeMode } = useThemeContext();
  const theme = createAppTheme(themeMode);

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <App />
    </MuiThemeProvider>
  );
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AppWithTheme />
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>
);